import { Button, Switch } from 'components/common';
import useBottomSheet from 'hooks/common/useBottomSheet';
import useThemeSetting from 'hooks/settings/useThemeSetting';
import type { ThemeCardSize } from 'hooks/settings/useThemeSetting';
import { useEffect } from 'react';

const ThemeSetting: React.FC = () => {
  const { closeWithAnimation } = useBottomSheet();
  const { userSelectedTheme, lightThemeCardSize, darkThemeCardSize, handleThemeCardClick, handleSwitchChange, handleConfirm } = useThemeSetting();

  // 열려있는 동안 색상 변경 애니메이션 추가
  useEffect(() => {
    const bottomSheetContainer = document.querySelector('[data-bottom-sheet-id="ThemeSetting"]');
    const DragBar = bottomSheetContainer?.querySelector('[data-component="DragBar"]');
    bottomSheetContainer?.classList.add('transition-colors-animation');
    DragBar?.classList.add('transition-colors-animation');

    return () => {
      bottomSheetContainer?.classList.remove('transition-colors-animation');
      DragBar?.classList.remove('transition-colors-animation');
    };
  }, []);

  const handleConfirmClick = () => {
    handleConfirm();
    closeWithAnimation('ThemeSetting');
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex w-full flex-col gap-[var(--spacing-sm)] rounded-lg bg-base-bg-subtle p-[var(--spacing-lg)]">
        <div className="flex h-[264px] max-h-[40dvh] items-center gap-[var(--spacing-sm)]">
          <ThemeCard theme="light" size={lightThemeCardSize} onClick={() => handleThemeCardClick('light')} />
          <ThemeCard theme="dark" size={darkThemeCardSize} onClick={() => handleThemeCardClick('dark')} />
        </div>
        <div className="h-[1px] w-full bg-base-border-default" />
        <div className="flex items-center justify-between">
          <div className="flex flex-1 flex-col gap-[var(--spacing-2xs)]">
            <div className="typography-body-sm text-base-txt-primary">{brsc.system_setting}</div>
            <div className="typography-caption-body text-base-txt-secondary">{brsc.setting_theme_system_desc}</div>
          </div>
          <Switch isOn={userSelectedTheme === 'system'} onChange={handleSwitchChange} />
        </div>
      </div>
      <Button style={{ text: brsc.confirm, color: 'btn-bg-primary', fontColor: 'btn-txt-primary' }} onClick={handleConfirmClick} />
    </div>
  );
};

export default ThemeSetting;

const THEME_CARD_SIZE_CLASS = {
  sm: 'flex-[5] h-5/6 opacity-30',
  md: 'flex-[6] h-full opacity-30',
  lg: 'flex-[7] h-full border-2 border-solid border-base-border-focus',
};

const ThemeCard: React.FC<{ theme: 'light' | 'dark'; size: ThemeCardSize; onClick: () => void }> = ({ theme, size, onClick }) => {
  // 전역 테마와 무관하게 고정된 테마 색상으로 덮어씌우기
  const themeStyles =
    theme === 'light'
      ? {
          '--bcs-semantic-base-bg-default': 'var(--wh-100)',
          '--bcs-semantic-base-bg-subtle': 'var(--gray-100)',
          '--bcs-semantic-base-txt-primary': 'var(--gray-800)',
          '--bcs-semantic-base-border-focus': 'var(--purple-250)',
          '--bcs-semantic-btn-bg-normal': 'var(--gray-200)',
          '--bcs-semantic-btn-bg-primary': 'var(--purple-250)',
        }
      : {
          '--bcs-semantic-base-bg-default': 'var(--bk-300)',
          '--bcs-semantic-base-bg-subtle': 'var(--bk-200)',
          '--bcs-semantic-base-txt-primary': 'var(--wh-100)',
          '--bcs-semantic-base-border-focus': 'var(--purple-200)',
          '--bcs-semantic-btn-bg-normal': 'var(--gray-750)',
          '--bcs-semantic-btn-bg-primary': 'var(--purple-200)',
        };

  return (
    <div
      style={{ ...themeStyles, contain: 'layout style paint', willChange: 'opacity' } as React.CSSProperties}
      className={`relative items-center rounded-md bg-base-bg-default transition-[flex,height,opacity] duration-300 ease-out ${THEME_CARD_SIZE_CLASS[size]}`}
      onClick={onClick}
    >
      <div className="typography-caption-head absolute left-1/2 top-2 -translate-x-1/2 whitespace-nowrap rounded-full bg-base-bg-subtle px-[var(--spacing-md)] py-[var(--spacing-xs)] text-center text-base-txt-primary">
        {theme === 'light' ? brsc.setting_theme_light : brsc.setting_theme_dark}
      </div>
      <img className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" src="/imgweb/icon/ic_shield.svg" alt="" />
      <div className="absolute bottom-[54px] left-2 right-2 h-2 max-w-[40px] rounded-sm bg-base-bg-subtle" />
      <div className="absolute bottom-[38px] left-2 right-2 h-3 max-w-[98px] rounded-sm bg-base-bg-subtle" />
      <div className="absolute bottom-0 left-0 right-0 flex gap-1 p-2">
        <div className="h-6 w-[30px] rounded-sm bg-btn-bg-normal" />
        <div className="h-6 flex-1 rounded-sm bg-btn-bg-primary" />
      </div>
    </div>
  );
};
